/**
 * Is the backend still producing parsed content / child cycles for a cycle?
 *
 * Its own module (no axios, no React) so the decision can be tested without
 * mounting the 1700-line viewer — the polling bug found in the 2026-08-21 prod
 * E2E was entirely a wrong answer to this one question:
 *
 *   A snapshot-mode webpage never populates sections or a screenshot (that IS
 *   its design), but the viewer used their absence as "still working", so the
 *   poll could only ever end by running out of attempts — and the 60 s ceiling
 *   expired 31 s before the snapshot finished and created the media children.
 *   Measured on cycle 6a87fcc5: created 07:22:45, child created 07:24:16.
 */
export interface ParsedPollInputs {
  contentType?: string | null;
  /** Cycle has a SharePoint file, i.e. an on-demand parse is possible. */
  hasFileId?: boolean;
  snapshotStatus?: 'pending' | 'ready' | 'failed' | null;
  hasSections: boolean;
  hasScreenshot: boolean;
  parsingInFlight?: boolean;
  scrapingInFlight?: boolean;
}

const PARSEABLE_FILE_TYPES = ['docx', 'pptx', 'pdf'];

export function isParsedContentWorkInFlight(i: ParsedPollInputs): boolean {
  const isWebpage = i.contentType === 'webpage';
  // Snapshot-mode capture reports its own status; the media children are
  // created when it finishes, so 'pending' is the trustworthy "keep waiting".
  const captureInFlight = isWebpage && (i.snapshotStatus === 'pending' || !!i.scrapingInFlight);
  // Legacy (non-snapshot) webpage cycles carry no snapshotStatus at all; for
  // those "no content yet" remains the only available signal.
  const legacyScrapeInFlight = isWebpage && !i.snapshotStatus && !i.hasSections && !i.hasScreenshot;
  const fileParseInFlight = PARSEABLE_FILE_TYPES.includes(i.contentType || '') &&
    !i.hasSections && (!!i.parsingInFlight || !!i.hasFileId);
  return captureInFlight || legacyScrapeInFlight || fileParseInFlight;
}
